<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type {
  ClientRbacCatalog,
  ClientRole,
  CreateClientRoleRequest,
  ModuleMetadata,
  UpdateClientRoleRequest
} from '../../types'

interface RoleDraft {
  mode: 'create' | 'edit'
  roleId: number | null
  moduleCode: string
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
}

const props = defineProps<{
  roles: ClientRole[]
  catalog: ClientRbacCatalog
  modules: ModuleMetadata[]
  canCreate: boolean
  canUpdate: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateClientRoleRequest]
  update: [roleId: number, payload: UpdateClientRoleRequest]
}>()

const filters = ref({ keyword: '', moduleCode: '' })
const showDraft = ref(false)
const draft = ref<RoleDraft>(emptyDraft())
const page = ref(1)
const pageSize = ref(20)

const moduleOptions = computed(() => [
  { value: '', label: '全部模块' },
  { value: 'system', label: '通用 C 端' },
  ...props.modules.filter((module) => module.moduleCode !== 'system').map((m) => ({ value: m.moduleCode, label: m.moduleName }))
])

const filteredRoles = computed(() => {
  const keyword = filters.value.keyword.trim().toLowerCase()
  return props.roles.filter((role) => {
    const matchesModule = !filters.value.moduleCode || role.moduleCode === filters.value.moduleCode
    const matchesKeyword = !keyword || role.code.toLowerCase().includes(keyword) ||
      role.name.toLowerCase().includes(keyword) || role.permissions.some((permission) => permission.includes(keyword))
    return matchesModule && matchesKeyword
  })
})

const paginatedRoles = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredRoles.value.slice(start, start + pageSize.value)
})

watch([() => filters.value.keyword, () => filters.value.moduleCode], () => {
  page.value = 1
})

const menuGroups = computed(() => {
  const groups = new Map<string, ClientRbacCatalog['menus']>()
  for (const menu of props.catalog.menus) {
    const rows = groups.get(menu.moduleCode) ?? []
    rows.push(menu)
    groups.set(menu.moduleCode, rows)
  }
  return Array.from(groups.entries()).map(([moduleCode, menus]) => ({
    moduleCode,
    moduleName: moduleName(moduleCode),
    menus
  }))
})

const permissionGroups = computed(() => {
  const groups = new Map<string, string[]>()
  for (const permission of props.catalog.permissions) {
    const moduleCode = permission.split(':')[0] || 'system'
    const rows = groups.get(moduleCode) ?? []
    rows.push(permission)
    groups.set(moduleCode, rows)
  }
  return Array.from(groups.entries()).map(([moduleCode, permissions]) => ({
    moduleCode,
    moduleName: moduleName(moduleCode),
    permissions
  }))
})

const selectedMenuPermissions = computed(() => props.catalog.menus
  .filter((menu) => draft.value.menuCodes.includes(menu.menuCode))
  .map((menu) => menu.permissionCode)
  .filter(Boolean))

const effectivePermissions = computed(() =>
  Array.from(new Set([...draft.value.permissions, ...selectedMenuPermissions.value])).sort()
)

function emptyDraft(): RoleDraft {
  return {
    mode: 'create',
    roleId: null,
    moduleCode: 'system',
    code: 'client_operator',
    name: 'C端运营用户',
    menuCodes: [],
    permissions: []
  }
}

function moduleName(moduleCode: string) {
  return moduleOptions.value.find((module) => module.value === moduleCode)?.label ?? moduleCode
}

function openCreate() {
  draft.value = emptyDraft()
  showDraft.value = true
}

function openEdit(role: ClientRole) {
  const menuPermissions = new Set(props.catalog.menus
    .filter((menu) => role.menuCodes.includes(menu.menuCode))
    .map((menu) => menu.permissionCode))
  draft.value = {
    mode: 'edit',
    roleId: role.id,
    moduleCode: role.moduleCode,
    code: role.code,
    name: role.name,
    menuCodes: [...role.menuCodes].sort(),
    permissions: role.permissions.filter((permission) => !menuPermissions.has(permission)).sort()
  }
  showDraft.value = true
}

function toggleValue(values: string[], value: string) {
  const selected = new Set(values)
  if (selected.has(value)) selected.delete(value)
  else selected.add(value)
  return Array.from(selected).sort()
}

function toggleMenu(menuCode: string) {
  draft.value.menuCodes = toggleValue(draft.value.menuCodes, menuCode)
}

function togglePermission(permission: string) {
  draft.value.permissions = toggleValue(draft.value.permissions, permission)
}

function submit() {
  if (!draft.value.name.trim() || !draft.value.code.trim()) return
  const payload = {
    name: draft.value.name.trim(),
    menuCodes: draft.value.menuCodes,
    permissions: effectivePermissions.value
  }
  if (draft.value.mode === 'create') {
    emit('create', {
      moduleCode: draft.value.moduleCode,
      code: draft.value.code.trim(),
      ...payload
    })
  } else if (draft.value.roleId != null) {
    emit('update', draft.value.roleId, payload)
  }
  showDraft.value = false
}

const columns: TableColumnType<ClientRole>[] = [
  { key: 'role', title: '角色', minWidth: 160 },
  { key: 'module', title: '所属模块', minWidth: 120 },
  { key: 'menus', title: '菜单', minWidth: 160 },
  { key: 'permissions', title: 'API 权限', minWidth: 160 },
  { key: 'actions', title: '操作', width: 100, align: 'right' }
]
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Client RBAC</p>
        <h2>C端角色</h2>
        <small>管理 sys_client_role、菜单授权和 API 权限</small>
      </div>
      <span>{{ filteredRoles.length }} / {{ roles.length }} 个角色</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="filters = { keyword: '', moduleCode: '' }">
      <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="角色、编码、权限" />
      <RrSelect v-model:value="filters.moduleCode" :options="moduleOptions" />
      <template #actions>
        <RrButton type="default" :disabled="loading" @click="filters = { keyword: '', moduleCode: '' }">重置</RrButton>
        <RrButton type="primary" :disabled="loading || !canCreate" @click="openCreate">新增角色</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showDraft"
      :title="draft.mode === 'create' ? '新增 C 端角色' : '编辑 C 端角色'"
      :width="600"
      @close="showDraft = false"
    >
      <RrForm
        layout="vertical"
        :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
        @submit.prevent="submit"
      >
        <div class="form-grid inline-form-grid">
          <RrFormItem label="所属模块">
            <RrSelect
              v-model:value="draft.moduleCode"
              :disabled="draft.mode === 'edit'"
              :options="moduleOptions.filter((m) => Boolean(m.value))"
            />
          </RrFormItem>
          <RrFormItem label="角色编码">
            <RrInput v-model:value.trim="draft.code" autocomplete="off" :disabled="draft.mode === 'edit'" />
          </RrFormItem>
          <RrFormItem label="角色名称">
            <RrInput v-model:value.trim="draft.name" autocomplete="off" />
          </RrFormItem>
        </div>

        <div class="permission-config-grid" style="margin-top: 16px;">
          <div class="permission-config-block">
            <div class="section-head" style="margin-bottom: 8px;">
              <h3 style="margin: 0; font-size: 14px;">C端菜单权限</h3>
              <span style="font-size: 12px; color: var(--rr-color-text-muted);">{{ draft.menuCodes.length }} 已选</span>
            </div>
            <div class="check-grid compact-check-grid" style="display: flex; flex-direction: column; gap: 8px;">
              <div v-for="group in menuGroups" :key="group.moduleCode" class="permission-group">
                <h4 style="margin: 8px 0 4px; font-size: 13px;">{{ group.moduleName }}</h4>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">
                  <label v-for="menu in group.menus" :key="menu.menuCode" class="check-row" style="display: flex; align-items: center; gap: 6px;">
                    <RrCheckbox
                      :checked="draft.menuCodes.includes(menu.menuCode)"
                      :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
                      @change="toggleMenu(menu.menuCode)"
                    />
                    <span><strong>{{ menu.name }}</strong><small style="margin-left: 4px; color: var(--rr-color-text-muted);">{{ menu.menuCode }}</small></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="permission-config-block" style="margin-top: 16px;">
            <div class="section-head" style="margin-bottom: 8px;">
              <h3 style="margin: 0; font-size: 14px;">C端 API 权限</h3>
              <span style="font-size: 12px; color: var(--rr-color-text-muted);">{{ effectivePermissions.length }} 生效</span>
            </div>
            <div class="check-grid compact-check-grid" style="display: flex; flex-direction: column; gap: 8px;">
              <div v-for="group in permissionGroups" :key="group.moduleCode" class="permission-group">
                <h4 style="margin: 8px 0 4px; font-size: 13px;">{{ group.moduleName }}</h4>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">
                  <label v-for="permission in group.permissions" :key="permission" class="check-row" style="display: flex; align-items: center; gap: 6px;">
                    <RrCheckbox
                      :checked="draft.permissions.includes(permission) || selectedMenuPermissions.includes(permission)"
                      :disabled="loading || selectedMenuPermissions.includes(permission) || (draft.mode === 'create' ? !canCreate : !canUpdate)"
                      @change="togglePermission(permission)"
                    />
                    <span style="font-size: 12px;">{{ permission }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RrForm>

      <template #footer>
        <RrButton @click="showDraft = false">取消</RrButton>
        <RrButton
          type="primary"
          :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
          @click="submit"
        >
          保存角色授权
        </RrButton>
      </template>
    </AdminDrawer>

    <AdminDataTable
      :columns="columns"
      :data-source="paginatedRoles"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="filteredRoles.length"
      row-key="id"
      :count="filteredRoles.length"
      :empty="filteredRoles.length === 0"
      empty-title="暂无匹配的 C 端角色"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'role'">
          <div>
            <strong>{{ record.name }}</strong>
            <small style="display: block; color: var(--rr-color-text-muted)">{{ record.code }} · ID {{ record.id }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'module'">
          {{ moduleName(record.moduleCode) }}
        </template>
        <template v-else-if="column.key === 'menus'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="code in record.menuCodes.slice(0, 3)" :key="code">{{ code }}</RrTag>
            <RrTag v-if="record.menuCodes.length > 3" tone="neutral">+{{ record.menuCodes.length - 3 }}</RrTag>
            <span v-if="record.menuCodes.length === 0" style="color: var(--rr-color-text-muted); font-size: 12px;">未授权</span>
          </div>
        </template>
        <template v-else-if="column.key === 'permissions'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="p in record.permissions.slice(0, 2)" :key="p">{{ p }}</RrTag>
            <RrTag v-if="record.permissions.length > 2" tone="neutral">+{{ record.permissions.length - 2 }}</RrTag>
            <span v-if="record.permissions.length === 0" style="color: var(--rr-color-text-muted); font-size: 12px;">未授权</span>
          </div>
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton type="link" :disabled="loading || !canUpdate" @click="openEdit(record)">
            编辑授权
          </RrButton>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
